import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stopAllRuns } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { ConfirmDialog } from './ConfirmDialog';

/** Danger-outline "Stop all running agents" control with a confirm step.
 *  `stoppable` is the count of currently-killable runs; 0 renders a faint note. */
export function StopAllButton({ stoppable }: { stoppable: number }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const stopAll = useMutation({
    mutationFn: stopAllRuns,
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: qk.activeRuns });
    },
  });

  if (stoppable === 0) {
    return <span className="text-xs text-ink-faint">No running agents to stop</span>;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm whitespace-nowrap border border-bad bg-transparent text-bad hover:bg-bad-soft"
        onClick={() => setOpen(true)}
      >
        Stop all running agents
      </button>
      <ConfirmDialog
        open={open}
        title="Stop all running agents"
        message={`Kill ${stoppable} running ${
          stoppable === 1 ? 'agent' : 'agents'
        }? Containers are stopped immediately; queued runs are unaffected.`}
        confirmLabel="Stop all"
        busy={stopAll.isPending}
        error={stopAll.isError ? errorMessage(stopAll.error, 'Could not stop agents') : null}
        onConfirm={() => stopAll.mutate()}
        onClose={() => {
          setOpen(false);
          stopAll.reset();
        }}
      />
    </>
  );
}
